import React from "react";
import styled from "styled-components";
import get from "lodash/get";
import filterString from "../common3/filterString";
import Edit from "./slateEditor";
import { injectIntl } from "react-intl";
import JSON5 from "json5";

const Strong = styled.strong``;
const Em = styled.em``;
const Code = styled.code``;
const U = styled.u``;
const A = styled.a``;
const OL = styled.ol``;
const UL = styled.ul``;
const LI = styled.li`
  text-align: ${(p) => p.align};
`;
const P = styled.p`
  text-align: ${(p) => p.align};
`;
const H1 = styled.h1`
  text-align: ${(p) => p.align};
`;
const H2 = styled.h2`
  text-align: ${(p) => p.align};
`;
const H3 = styled.h3`
  text-align: ${(p) => p.align};
`;
const H4 = styled.h4`
  text-align: ${(p) => p.align};
`;
const H5 = styled.h5`
  text-align: ${(p) => p.align};
`;
const H6 = styled.h6`
  text-align: ${(p) => p.align};
`;
const BlockQuote = styled.blockquote`
  padding: 0 30px;
  text-align: ${(p) => p.align};
`;

const Text = (props) => {
  const { builder = false, extra = {}, fromSummary, intl } = props;
  const { editInPortal = false } = extra;
  const disabled = props.disabled || extra.disabled;

  if (!disabled && !builder && editInPortal && !fromSummary) {
    return <Edit {...props} state="entered" portalEdit={true} />;
  }

  const mapChildren = (children) => {
    return children.map(
      (
        { type, align, children, text, bold, italic, code, underline, url },
        i
      ) => {
        if (bold) {
          return <Strong key={i}>{text}</Strong>;
        }

        if (italic) {
          return <Em key={i}>{text}</Em>;
        }

        if (code) {
          return <Code key={i}>{text}</Code>;
        }

        if (underline) {
          return <U key={i}>{text}</U>;
        }

        if (type === "link") {
          return (
            <A key={i} href={url} target="_blank">
              {mapChildren(children)}
            </A>
          );
        }

        if (type === "list-item") {
          return (
            <LI key={i} align={align}>
              {mapChildren(children)}
            </LI>
          );
        }

        if (!text) {
          return <span key={i}>&nbsp;&nbsp;</span>;
        }

        return text;
      }
    );
  };

  const _text = editInPortal
    ? get(props, "value", null)
    : get(props, "extra.text", null);

  if (!_text) {
    if (editInPortal) {
      return <div>Text component is only editable in portal</div>;
    }

    return <div>{intl.formatMessage({ id: "Text.AddText" })}</div>;
  }

  let text = _text;
  if (!builder) {
    const filteredString = filterString(JSON.stringify(_text), props);
    const replacedString = filteredString
      .replace(/\t/g, " ")
      .replace(/[\n]/g, "\\n")
      .replace(/\\(?!["\\/bfnrtu])/g, "\\\\");

    try {
      text = JSON.parse(replacedString);
    } catch (err) {
      console.error(
        "[Text/index] Could not parse string. Trying with json5.",
        err
      );
    }
    try {
      text = JSON5.parse(replacedString);
    } catch (err) {
      console.error(
        "[Text/index] Could not parse string. Trying with escaping less characters.",
        err
      );
    }
    try {
      text = JSON.parse(
        filteredString.replace(/\t/g, " ").replace(/[\n]/g, "\\n")
      );
    } catch (err) {
      console.error("[Text/index] Could not parse string.", err);
    }
  }

  return text.map(({ type, align, children }, i) => {
    if (type === "paragraph") {
      return (
        <P key={i} align={align}>
          {mapChildren(children)}
        </P>
      );
    }

    if (type === "block-quote") {
      return (
        <BlockQuote key={i} align={align}>
          {mapChildren(children)}
        </BlockQuote>
      );
    }

    if (type === "heading-one") {
      return (
        <H1 key={i} align={align}>
          {mapChildren(children)}
        </H1>
      );
    }

    if (type === "heading-two") {
      return (
        <H2 key={i} align={align}>
          {mapChildren(children)}
        </H2>
      );
    }

    if (type === "heading-three") {
      return (
        <H3 key={i} align={align}>
          {mapChildren(children)}
        </H3>
      );
    }

    if (type === "heading-four") {
      return (
        <H4 key={i} align={align}>
          {mapChildren(children)}
        </H4>
      );
    }

    if (type === "heading-five") {
      return (
        <H5 key={i} align={align}>
          {mapChildren(children)}
        </H5>
      );
    }

    if (type === "heading-six") {
      return (
        <H6 key={i} align={align}>
          {mapChildren(children)}
        </H6>
      );
    }

    if (type === "numbered-list") {
      return <OL key={i}>{mapChildren(children)}</OL>;
    }

    if (type === "bulleted-list") {
      return <UL key={i}>{mapChildren(children)}</UL>;
    }
  });
};

export default injectIntl(Text);
